import { useState, useCallback, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { authFetch } from '../lib/authFetch';

/**
 * Custom hook for managing user follows (counterparts, technologies, countries)
 *
 * Uses module-level shared state so ALL useFollows() instances stay in sync.
 * When any FollowButton toggles, the BloombergDashboard tabs, YourFollows
 * column, and every other consumer update immediately — no hard refresh needed.
 */

// Shared state — single source of truth across all hook instances
let _follows = [];
let _loaded = false;
let _loadingPromise = null;
let _userId = null;
const _listeners = new Set();

function _setFollows(newFollows) {
  _follows = newFollows;
  _listeners.forEach(fn => fn([..._follows]));
}

export function useFollows() {
  const { user, isSignedIn } = useUser();
  const [follows, setFollows] = useState(_follows);
  const [loading, setLoading] = useState(!_loaded);

  // Subscribe to shared state changes
  useEffect(() => {
    _listeners.add(setFollows);
    // Sync with current shared state on mount
    if (_loaded) setFollows([..._follows]);
    return () => _listeners.delete(setFollows);
  }, []);

  // Load follows from API — only one fetch across all instances
  useEffect(() => {
    if (!isSignedIn || !user?.id) {
      // Signed out — clear shared state
      if (_loaded || _follows.length > 0) {
        _follows = [];
        _loaded = false;
        _userId = null;
        _loadingPromise = null;
        _listeners.forEach(fn => fn([]));
      }
      setLoading(false);
      return;
    }

    // Different user signed in — reset
    if (_userId && _userId !== user.id) {
      _follows = [];
      _loaded = false;
      _loadingPromise = null;
    }

    // Already loaded for this user
    if (_loaded && _userId === user.id) {
      setLoading(false);
      return;
    }

    // Another instance is already fetching — wait for it
    if (_loadingPromise) {
      _loadingPromise.then(() => setLoading(false));
      return;
    }

    _userId = user.id;
    setLoading(true);
    _loadingPromise = authFetch(`/api/follows?userId=${user.id}`)
      .then(r => r.json())
      .then(result => {
        _follows = result.follows || [];
        _loaded = true;
        _listeners.forEach(fn => fn([..._follows]));
      })
      .catch(err => console.error('Error loading follows:', err))
      .finally(() => { setLoading(false); _loadingPromise = null; });
  }, [isSignedIn, user?.id]);

  // Check if a specific target is followed
  const isFollowing = useCallback((targetType, targetId) => {
    return follows.some(f => f.target_type === targetType && f.target_id === targetId);
  }, [follows]);

  // Toggle follow — optimistic UI, shared across all instances
  const toggle = useCallback(async (targetType, targetId) => {
    if (!isSignedIn || !user?.id) return false;

    const currently = _follows.some(f => f.target_type === targetType && f.target_id === targetId);

    if (currently) {
      // Optimistic remove — all instances update immediately
      _setFollows(_follows.filter(f => !(f.target_type === targetType && f.target_id === targetId)));
      try {
        await authFetch('/api/follows', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.id, targetType, targetId }),
        });
      } catch (err) {
        console.error('Error removing follow:', err);
        _setFollows([..._follows, { target_type: targetType, target_id: targetId, created_at: new Date().toISOString() }]);
      }
    } else {
      // Optimistic add — all instances update immediately
      const optimistic = { target_type: targetType, target_id: targetId, created_at: new Date().toISOString() };
      _setFollows([optimistic, ..._follows]);
      try {
        window.gtag?.('event', 'follow_created', { target_type: targetType, target_id: targetId });
      } catch (e) { /* silent */ }
      try {
        await authFetch('/api/follows', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.id, targetType, targetId }),
        });
      } catch (err) {
        console.error('Error creating follow:', err);
        _setFollows(_follows.filter(f => !(f.target_type === targetType && f.target_id === targetId)));
      }
    }

    return !currently;
  }, [isSignedIn, user?.id]);

  return { follows, loading, isFollowing, toggle, isSignedIn };
}
